import React, { useEffect, useState } from "react";
import api from "../api";
import {
  X, Upload, Loader2, CheckCircle2, AlertCircle, FileText, ArrowRight,
  RefreshCw,
} from "lucide-react";

const FIELD_LABELS = {
  companyName: "Ragione sociale *",
  email: "Email",
  website: "Sito web",
  phone: "Telefono",
  pec: "PEC",
  city: "Città",
  province: "Provincia",
  address: "Indirizzo",
  zipCode: "CAP",
  vatCode: "P.IVA",
  taxCode: "Codice Fiscale",
  ateco: "Codice ATECO",
  atecoDescr: "Settore / Descrizione attività",
  owner_fullName: "Titolare / Referente",
};

const STEPS = ["upload", "preview", "config", "done"];

export default function CsvImportModal({ onClose, onImported }) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(true);
  const [lists, setLists] = useState([]);
  const [targetMode, setTargetMode] = useState("new"); // "new" | "existing" | "none"
  const [newListName, setNewListName] = useState("");
  const [existingListId, setExistingListId] = useState("");
  const [sourceLabel, setSourceLabel] = useState("csv_import");
  const [mergeMissing, setMergeMissing] = useState(true);
  const [verifyEmails, setVerifyEmails] = useState(false);
  const [zbConfigured, setZbConfigured] = useState(null); // null=unknown, true/false
  const [busy, setBusy] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/lists").then(r => setLists(r.data || []));
    // Detect if ZeroBounce is configured (used to grey-out the toggle)
    api.get("/email/verify/credits")
      .then(r => setZbConfigured(r.data?.credits != null && r.data.credits >= 0))
      .catch(() => setZbConfigured(false));
  }, []);

  const runPreview = async (f, headerFlag = hasHeader) => {
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("has_header", String(headerFlag));
      const { data } = await api.post("/crm/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.auto_mapping || {});
      setStep("preview");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // Re-run preview with the current file when the header flag is toggled
  const toggleHeader = async (val) => {
    setHasHeader(val);
    if (file) await runPreview(file, val);
  };

  // Debounced recompute when the user edits the mapping
  useEffect(() => {
    if (!file || !preview || step !== "preview") return;
    const t = setTimeout(async () => {
      setRecomputing(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mapping_json", JSON.stringify(mapping));
        fd.append("has_header", String(hasHeader));
        const { data } = await api.post("/crm/import/recompute", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPreview(p => ({ ...p, ...data }));
      } catch (e) {
        // soft fail — keep showing previous counters
      } finally { setRecomputing(false); }
    }, 600);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(mapping)]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const execute = async () => {
    if (!file || !mapping.companyName && !mapping.website && !mapping.email && !mapping.vatCode) {
      setError("Mappa almeno una colonna identificativa (Ragione sociale, Sito web, Email o P.IVA)");
      return;
    }
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping_json", JSON.stringify(mapping));
      fd.append("merge_missing", String(mergeMissing));
      fd.append("verify_emails", String(verifyEmails && zbConfigured === true));
      fd.append("source_label", sourceLabel || "csv_import");
      fd.append("has_header", String(hasHeader));
      if (targetMode === "new" && newListName.trim()) {
        fd.append("new_list_name", newListName.trim());
      } else if (targetMode === "existing" && existingListId) {
        fd.append("list_id", existingListId);
      }
      const { data } = await api.post("/crm/import/execute", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStep("done");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const close = () => {
    if (step === "done" && onImported) onImported(result);
    onClose();
  };

  const unmappedHeaders = (preview?.headers || []).filter(
    h => !Object.values(mapping).includes(h)
  );

  return (
    <div className="modal-backdrop" onClick={close} data-testid="csv-import-modal">
      <div className="modal" onClick={e => e.stopPropagation()}
        style={{maxWidth: 920, padding: 0, maxHeight: "90vh", display:"flex", flexDirection:"column"}}>
        <div style={{padding: "16px 18px", borderBottom: "1px solid #eee",
          display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <div className="flex items-center gap-2">
            <Upload size={18} color="var(--mv-primary)"/>
            <div style={{fontWeight: 600, fontSize: 16}}>Importa CSV liste contatti</div>
          </div>
          <button onClick={close} className="btn btn-ghost btn-sm" data-testid="csv-close"><X size={16}/></button>
        </div>

        {/* Stepper */}
        <div style={{padding: "10px 18px", borderBottom: "1px solid #f0f0f0", background: "#fafafa"}}>
          <div className="flex items-center gap-2 text-small">
            {[
              {key:"upload", label:"1. Carica file"},
              {key:"preview", label:"2. Verifica mapping"},
              {key:"config", label:"3. Configura"},
              {key:"done", label:"4. Risultato"},
            ].map((s, i, arr) => (
              <React.Fragment key={s.key}>
                <span style={{
                  fontWeight: 600,
                  color: STEPS.indexOf(step) >= i ? "var(--mv-primary)" : "var(--mv-text-muted)",
                }}>{s.label}</span>
                {i < arr.length - 1 && <ArrowRight size={12} color="#ccc"/>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{flex: 1, overflowY: "auto", padding: 18}}>

        {error && (
          <div className="card mb-3" style={{padding: 12, background: "#fee2e2", borderLeft: "4px solid var(--mv-danger)"}}>
            <div className="flex items-start gap-2">
              <AlertCircle size={16} color="var(--mv-danger)" style={{marginTop:2}}/>
              <div className="text-small" style={{color: "#991b1b"}}><strong>Errore:</strong> {error}</div>
            </div>
          </div>
        )}

        {/* STEP 1: Upload */}
        {step === "upload" && (
          <div data-testid="csv-step-upload">
            <div className="text-small text-muted mb-3">
              Carica un file <strong>CSV</strong> (UTF-8, max 5MB) con le tue liste di contatti acquistate.
              Il sistema riconosce automaticamente colonne <strong>italiane ed inglesi</strong> (Ragione Sociale,
              Email, Sito Web, P.IVA, Telefono, Città, Provincia, Indirizzo, Settore, Titolare, ecc.).
              Delimiter <code>;</code> o <code>,</code> auto-detected.
            </div>
            <div style={{
              border: "2px dashed var(--mv-border)", borderRadius: 8, padding: 36, textAlign: "center",
              background: "#fafafa", cursor: "pointer",
            }}>
              {busy ? (
                <div className="text-muted">
                  <Loader2 size={28} className="spin" style={{margin: "0 auto 6px"}}/>
                  Analisi CSV in corso…
                </div>
              ) : (
                <>
                  <FileText size={36} color="#cbd5e1" style={{margin: "0 auto 8px"}}/>
                  <div className="mb-2">Clicca per selezionare o trascina qui un file CSV</div>
                  <input type="file" accept=".csv,text/csv" onChange={onFileChange}
                    data-testid="csv-file-input" disabled={busy}
                    style={{display: "block", margin: "0 auto"}}/>
                </>
              )}
            </div>
            <div className="card mt-3" style={{padding: 12, background: "#fff7ed"}}>
              <div className="text-small">
                <strong>💡 Suggerimento:</strong> se il tuo file è in formato <code>.xlsx</code> Excel,
                aprilo in Excel/Numbers e salvalo come "CSV UTF-8".
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Preview + mapping */}
        {step === "preview" && preview && (
          <div data-testid="csv-step-preview">
            {preview.header_warning && (
              <div className="card mb-3" style={{padding:12, background:"#fef3c7", borderLeft:"4px solid #f59e0b"}} data-testid="header-warning">
                <div className="flex gap-2 items-start">
                  <AlertCircle size={16} color="#92400e" style={{marginTop:2,flexShrink:0}}/>
                  <div className="text-small" style={{lineHeight:1.5, color:"#78350f"}}>
                    {preview.header_warning}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-3" style={{
              background:"var(--mv-bg-soft)", borderRadius:8, padding:"8px 12px"
            }}>
              <label className="flex items-center gap-2 text-small" style={{cursor:"pointer"}}>
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={e=>toggleHeader(e.target.checked)}
                  data-testid="has-header-toggle"
                  disabled={busy}/>
                <strong>La prima riga contiene le intestazioni</strong>
                <span className="text-muted">— togli la spunta se il tuo CSV inizia direttamente con i dati</span>
              </label>
              {recomputing && (
                <span className="text-small text-muted" style={{display:"inline-flex",alignItems:"center",gap:4}}>
                  <Loader2 size={13} className="animate-spin"/> Ricalcolo…
                </span>
              )}
            </div>

            <div className="grid gap-3 mb-4" style={{gridTemplateColumns: "repeat(4, 1fr)"}}>
              <KpiBox label="Righe totali" value={preview.total_rows}/>
              <KpiBox label="Nuove aziende" value={preview.counters.new} color="var(--mv-success)"/>
              <KpiBox label="Aggiornamenti (dedupe)" value={preview.counters.update} color="#1e40af"/>
              <KpiBox label="Righe ignorate"
                value={preview.counters.skip_no_identity + preview.counters.invalid_email}
                color={preview.counters.skip_no_identity > 0 ? "var(--mv-danger)" : "#888"}/>
            </div>

            {preview.total_rows > (preview.rows_analyzed || 5000) && (
              <div className="text-small text-muted mb-3" style={{padding:"6px 12px",
                background:"#eff6ff", borderLeft:"3px solid #3b82f6", borderRadius:4}}>
                <strong>Nota:</strong> il preview campiona le prime {preview.rows_analyzed || 5000} righe
                per velocità. L'import reale processerà tutte le {preview.total_rows} righe del file.
              </div>
            )}

            <div style={{fontWeight: 600, marginBottom: 8}}>Mappatura colonne</div>
            <div className="text-small text-muted mb-3">
              Il sistema ha provato a mappare automaticamente le tue colonne sui campi del CRM.
              Verifica e correggi se necessario. Le colonne <em>non mappate</em> verranno ignorate.
            </div>

            <div style={{border: "1px solid var(--mv-border)", borderRadius: 8, overflow: "hidden"}}>
              <table className="w-full" style={{borderCollapse: "collapse", fontSize: 13}}>
                <thead>
                  <tr style={{background: "#fafafa", fontSize: 11, textTransform: "uppercase", color: "var(--mv-text-muted)"}}>
                    <th style={{padding: "8px 12px", textAlign: "left", width: "45%"}}>Colonna nel CSV</th>
                    <th style={{padding: "8px 12px", textAlign: "left"}}>Campo nel CRM</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map(h => (
                    <tr key={h} style={{borderTop: "1px solid #f0f0f0"}}>
                      <td style={{padding: "8px 12px", fontWeight: 500}}>
                        {h}
                        <div className="text-small text-muted" style={{fontWeight: 400}}>
                          es. "{(preview.sample[0]?.[Object.entries(mapping).find(([_,v])=>v===h)?.[0]] || "—").toString().substring(0,30)}"
                        </div>
                      </td>
                      <td style={{padding: "8px 12px"}}>
                        <select className="input" style={{height: 32, padding: "2px 8px"}}
                          value={Object.entries(mapping).find(([_,v])=>v===h)?.[0] || ""}
                          onChange={e => {
                            const newField = e.target.value;
                            const newMap = { ...mapping };
                            // Remove any field currently mapped to this header
                            for (const k of Object.keys(newMap)) {
                              if (newMap[k] === h) delete newMap[k];
                            }
                            if (newField) {
                              // Remove conflicting field if it was mapped elsewhere
                              delete newMap[newField];
                              newMap[newField] = h;
                            }
                            setMapping(newMap);
                          }}
                          data-testid={`map-${h.replace(/\s+/g, "-").toLowerCase()}`}>
                          <option value="">— Non mappare (ignora) —</option>
                          {Object.entries(FIELD_LABELS).map(([k, label]) => (
                            <option key={k} value={k}>{label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{fontWeight: 600, marginTop: 16, marginBottom: 8}}>Anteprima righe ({preview.sample.length})</div>
            <div style={{border: "1px solid var(--mv-border)", borderRadius: 8, overflowX: "auto", maxHeight: 240, overflowY:"auto"}}>
              <table className="w-full" style={{borderCollapse: "collapse", fontSize: 12}}>
                <thead style={{position:"sticky", top:0, background:"#fafafa"}}>
                  <tr style={{fontSize: 10, textTransform: "uppercase", color: "var(--mv-text-muted)"}}>
                    <th style={{padding: "6px 8px", textAlign: "left"}}>Riga</th>
                    <th style={{padding: "6px 8px", textAlign: "left"}}>Azione</th>
                    <th style={{padding: "6px 8px", textAlign: "left"}}>Ragione sociale</th>
                    <th style={{padding: "6px 8px", textAlign: "left"}}>Email</th>
                    <th style={{padding: "6px 8px", textAlign: "left"}}>Sito</th>
                    <th style={{padding: "6px 8px", textAlign: "left"}}>Tel.</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((row, i) => (
                    <tr key={i} style={{borderTop: "1px solid #f0f0f0"}}>
                      <td style={{padding: "6px 8px", color: "var(--mv-text-muted)"}}>{row.row}</td>
                      <td style={{padding: "6px 8px"}}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4,
                          background: row.action === "new" ? "#dcfce7" : "#dbeafe",
                          color: row.action === "new" ? "#15803d" : "#1e40af",
                        }}>
                          {row.action === "new" ? "+ Nuova" : "↻ Aggiorna"}
                        </span>
                        {row.fields_to_merge?.length > 0 && (
                          <div className="text-small text-muted" style={{fontSize:10, marginTop:2}}>
                            +{row.fields_to_merge.length} campi
                          </div>
                        )}
                      </td>
                      <td style={{padding: "6px 8px"}}>{row.companyName || "—"}</td>
                      <td style={{padding: "6px 8px"}}>{row.email || "—"}</td>
                      <td style={{padding: "6px 8px"}}>{row.website || "—"}</td>
                      <td style={{padding: "6px 8px"}}>{row.phone || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {unmappedHeaders.length > 0 && (
              <div className="text-small text-muted mt-2">
                ⚠ Colonne <strong>non mappate</strong>: {unmappedHeaders.join(", ")} — saranno ignorate.
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Config */}
        {step === "config" && preview && (
          <div data-testid="csv-step-config">
            <div className="card mb-3" style={{padding: 14, background: "#fff7ed"}}>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} color="var(--mv-primary)"/>
                <div className="text-small">
                  Pronto per importare <strong>{preview.counters.new} nuove aziende</strong>
                  {preview.counters.update > 0 && <> e aggiornare <strong>{preview.counters.update} esistenti</strong></>}.
                </div>
              </div>
            </div>

            <div style={{fontWeight: 600, marginBottom: 8}}>Destinazione</div>
            <div className="grid gap-2 mb-4">
              <label className="flex items-center gap-2" style={{cursor: "pointer"}}>
                <input type="radio" checked={targetMode === "new"} onChange={()=>setTargetMode("new")}
                  data-testid="target-new"/>
                <span>Crea una <strong>nuova lista</strong> e aggiungi le aziende importate</span>
              </label>
              {targetMode === "new" && (
                <input className="input" placeholder="Nome della nuova lista (es. Lista PagineGialle Lombardia)"
                  value={newListName} onChange={e=>setNewListName(e.target.value)}
                  data-testid="new-list-name"
                  style={{marginLeft: 24, maxWidth: 500}}/>
              )}
              <label className="flex items-center gap-2" style={{cursor: "pointer"}}>
                <input type="radio" checked={targetMode === "existing"} onChange={()=>setTargetMode("existing")}
                  data-testid="target-existing"/>
                <span>Aggiungi a una <strong>lista esistente</strong></span>
              </label>
              {targetMode === "existing" && (
                <select className="input" value={existingListId}
                  onChange={e=>setExistingListId(e.target.value)}
                  data-testid="existing-list-select"
                  style={{marginLeft: 24, maxWidth: 500}}>
                  <option value="">— Seleziona —</option>
                  {lists.map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.totalItems || 0})</option>
                  ))}
                </select>
              )}
              <label className="flex items-center gap-2" style={{cursor: "pointer"}}>
                <input type="radio" checked={targetMode === "none"} onChange={()=>setTargetMode("none")}/>
                <span>Solo CRM (nessuna lista) — potrai aggiungerle a una lista in seguito</span>
              </label>
            </div>

            <div style={{fontWeight: 600, marginBottom: 8}}>Dedupe</div>
            <label className="flex items-start gap-2 mb-4" style={{cursor: "pointer"}}>
              <input type="checkbox" checked={mergeMissing}
                onChange={e=>setMergeMissing(e.target.checked)}
                data-testid="merge-missing"
                style={{marginTop: 3}}/>
              <div>
                <div><strong>Aggiorna i campi mancanti</strong> nelle aziende già esistenti</div>
                <div className="text-small text-muted">
                  Se trovo un'azienda già nel CRM (matching su sito/email/P.IVA) e nel CSV ci sono dati che mancano nel CRM (es. telefono), li aggiungo. I dati esistenti non vengono mai sovrascritti.
                </div>
              </div>
            </label>

            <div style={{fontWeight: 600, marginBottom: 8}}>Verifica email</div>
            <label className="flex items-start gap-2 mb-2"
              style={{cursor: zbConfigured === false ? "not-allowed" : "pointer",
                       opacity: zbConfigured === false ? 0.55 : 1}}>
              <input type="checkbox" checked={verifyEmails && zbConfigured === true}
                disabled={zbConfigured === false}
                onChange={e=>setVerifyEmails(e.target.checked)}
                data-testid="verify-emails"
                style={{marginTop: 3}}/>
              <div>
                <div>
                  <strong>Verifica email tramite ZeroBounce</strong> in background
                  {zbConfigured === true && <span style={{color:"var(--mv-success)",marginLeft:6,fontSize:12,fontWeight:600}}>● Attivo</span>}
                  {zbConfigured === false && <span style={{color:"var(--mv-danger)",marginLeft:6,fontSize:12,fontWeight:600}}>● Chiave non configurata</span>}
                </div>
                <div className="text-small text-muted">
                  {zbConfigured === false ? (
                    <>La <code>ZEROBOUNCE_API_KEY</code> non è impostata. Senza chiave, l'opzione è disabilitata.</>
                  ) : (
                    <>
                      L'import termina <strong>subito</strong> e ZeroBounce parte in background
                      (può durare diversi minuti per liste grandi). Puoi sempre lanciare la
                      verifica anche dopo, dalla pagina della lista (bottone "Verifica email").
                      <strong> Lascia disattivato se la lista è già stata verificata</strong> da un altro tool (~€0,007/email).
                    </>
                  )}
                </div>
              </div>
            </label>

            <div style={{fontWeight: 600, marginTop: 16, marginBottom: 8}}>Etichetta sorgente</div>
            <input className="input" placeholder="csv_import"
              value={sourceLabel} onChange={e=>setSourceLabel(e.target.value)}
              data-testid="source-label"
              style={{maxWidth: 400}}/>
            <div className="text-small text-muted mt-1">
              Per tracciare la provenienza (es. "paginegialle_2026q1", "linkedin_export", "fornitore_xyz").
            </div>
          </div>
        )}

        {/* STEP 4: Done */}
        {step === "done" && result && (
          <div data-testid="csv-step-done">
            <div className="text-center mb-4" style={{padding: 20}}>
              <CheckCircle2 size={48} color="var(--mv-success)" style={{margin: "0 auto 10px"}}/>
              <div style={{fontSize: 18, fontWeight: 600}}>Import completato!</div>
            </div>
            <div className="grid gap-3 mb-4" style={{gridTemplateColumns: "repeat(3, 1fr)"}}>
              <KpiBox label="Aziende nuove create" value={result.new_companies} color="var(--mv-success)"/>
              <KpiBox label="Aziende aggiornate" value={result.merged} color="#1e40af"/>
              <KpiBox label="Aggiunte alla lista" value={result.added_to_list} color="var(--mv-primary)"/>
            </div>
            {(result.skipped_no_identity + result.skipped_duplicate_in_csv + result.errors) > 0 && (
              <div className="card" style={{padding: 12, background: "#fef3c7"}}>
                <div className="text-small">
                  <strong>Note:</strong>
                  {result.skipped_no_identity > 0 && <> · {result.skipped_no_identity} righe ignorate (nessun identificativo)</>}
                  {result.skipped_duplicate_in_csv > 0 && <> · {result.skipped_duplicate_in_csv} duplicati interni al CSV</>}
                  {result.errors > 0 && <> · {result.errors} errori</>}
                </div>
              </div>
            )}
            {result.verify_result && result.verify_result.status === "queued_background" && (
              <div className="card mt-3" style={{padding: 14, background: "#eff6ff", borderLeft: "4px solid #3b82f6"}}>
                <div style={{fontWeight: 600, marginBottom: 6}}>✉ Verifica email avviata in background</div>
                <div className="text-small" style={{lineHeight: 1.6}}>
                  {result.verify_result.queued} email in coda per ZeroBounce. La verifica gira
                  in background (può durare diversi minuti per liste grandi). Apri la lista
                  per seguire i progressi — la verifica continua anche se chiudi questa finestra.
                </div>
              </div>
            )}
            {result.verify_result && result.verify_result.verified != null && !result.verify_result.error && (
              <div className="card mt-3" style={{padding: 14, background: "#eff6ff", borderLeft: "4px solid #1e40af"}}>
                <div style={{fontWeight: 600, marginBottom: 6}}>✉ Verifica email completata (ZeroBounce)</div>
                <div className="text-small" style={{lineHeight: 1.7}}>
                  <span style={{color:"var(--mv-success)"}}>● {result.verify_result.verified} verificate ora</span>
                  <span className="text-muted" style={{marginLeft: 12}}>○ {result.verify_result.cached} cache (&lt;60gg)</span>
                  <span className="text-muted" style={{marginLeft: 12}}>○ {result.verify_result.no_email} senza email</span>
                  {result.verify_result.errors > 0 && <span style={{color:"var(--mv-danger)",marginLeft: 12}}>● {result.verify_result.errors} errori</span>}
                </div>
                {result.verify_result.by_status && Object.keys(result.verify_result.by_status).length > 0 && (
                  <div className="text-small mt-2" style={{lineHeight:1.6}}>
                    <strong>Status:</strong> {Object.entries(result.verify_result.by_status).map(([s, n]) => (
                      <span key={s} style={{display:"inline-block",margin:"0 4px",padding:"1px 7px",borderRadius:4,
                        background: s === "valid" ? "#dcfce7" : s === "invalid" || s === "spamtrap" || s === "abuse" ? "#fee2e2" : "#fef3c7",
                        color: s === "valid" ? "#15803d" : s === "invalid" || s === "spamtrap" || s === "abuse" ? "#991b1b" : "#92400e",
                      }}>{s}: {n}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {result.verify_result?.error && (
              <div className="card mt-3" style={{padding: 12, background: "#fee2e2", borderLeft: "4px solid var(--mv-danger)"}}>
                <div className="text-small" style={{color: "#991b1b"}}>
                  ⚠ Verifica email fallita: {result.verify_result.error}
                </div>
              </div>
            )}
          </div>
        )}

        </div>

        {/* Footer */}
        <div style={{padding: "12px 18px", borderTop: "1px solid #eee",
          display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <div>
            {step === "preview" && (
              <button onClick={() => { setStep("upload"); setFile(null); setPreview(null); }}
                className="btn btn-ghost btn-sm" data-testid="back-upload">
                <RefreshCw size={13}/> Carica un altro file
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {step !== "done" && <button onClick={close} className="btn btn-ghost">Annulla</button>}
            {step === "preview" && (
              <button onClick={() => setStep("config")} className="btn btn-primary"
                disabled={!mapping.companyName && !mapping.website && !mapping.email && !mapping.vatCode}
                data-testid="goto-config">
                Continua <ArrowRight size={14}/>
              </button>
            )}
            {step === "config" && (
              <>
                <button onClick={() => setStep("preview")} className="btn btn-ghost">Indietro</button>
                <button onClick={execute} disabled={busy || ((preview?.counters?.new || 0) + (preview?.counters?.update || 0) === 0)}
                  className="btn btn-primary" data-testid="execute-import">
                  {busy ? <Loader2 size={14} className="spin"/> : <Upload size={14}/>}
                  {(() => {
                    const n = preview?.counters?.new || 0;
                    const u = preview?.counters?.update || 0;
                    if (n && u) return `Importa ${n} nuove + aggiorna ${u}`;
                    if (n) return `Importa ${n} aziende`;
                    if (u) return `Aggiorna ${u} esistenti`;
                    return "Nessuna azienda da importare";
                  })()}
                </button>
              </>
            )}
            {step === "done" && (
              <button onClick={close} className="btn btn-primary" data-testid="done-close">
                Chiudi e aggiorna
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function KpiBox({ label, value, color }) {
  return (
    <div style={{padding: 12, border: "1px solid var(--mv-border)", borderRadius: 8, background: "#fff"}}>
      <div className="text-small text-muted" style={{textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5, fontWeight: 600}}>
        {label}
      </div>
      <div style={{fontSize: 22, fontWeight: 700, color: color || "var(--mv-text)", marginTop: 2}}>
        {value}
      </div>
    </div>
  );
}
